import React, { ReactNode, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { JourneyAlias } from 'src/common/interfaces/journey/alias.interface';
import { useNotificationMessage } from 'src/common/notification/message.hook';

import { FormikAutoSave, JourneyForm, JourneyFormik } from 'src/common/JourneyFormik';

import { apiCreateJourneyAlias, apiUpdateJourneyAlias } from 'src/utils/journeyApi';
import { JourneyAliasFormType } from '../form/link-config-form.factory';
import validationSchema from '../form/validation';
import { preventSubmitOnEnterPressed } from 'src/common/helpers/prevent-submit-on-enter-pressed';
import { useSharePanelStore } from 'src/common/store/share.store';
import { ErrorInterface } from 'src/common/interfaces/error.interface';
import { ShareContentCommonProps } from '../slide-over/content';

interface ShareContentProps extends ShareContentCommonProps {
  children: ReactNode;
}

const ShareFormik = ({ onAliasUpdate, children }: ShareContentProps) => {
  const { setErrorNotification } = useNotificationMessage();
  const { journey, activeAlias, activeLinkForm, updateActiveLinkForm, setActiveAlias, setIsSaving } =
    useSharePanelStore((state) => ({
      journey: state.sharedJourney,
      activeAlias: state.activeAlias,
      activeLinkForm: state.activeLinkForm,
      setActiveAlias: state.setActiveAlias,
      updateActiveLinkForm: state.updateActiveLinkForm,
      setIsSaving: state.setIsSaving,
    }));

  const onFormSubmit = useCallback(
    (values: JourneyAliasFormType, actions: any) => {
      try {
        const fn = activeAlias?.uuid
          ? apiUpdateJourneyAlias(activeAlias?.uuid, values)
          : apiCreateJourneyAlias(journey?.uuid, values);

        fn.then((updatedAlias: JourneyAlias) => {
          const alias: JourneyAlias = {
            ...updatedAlias,
            uuid: updatedAlias.uuid,
          };
          setActiveAlias(alias);
          onAliasUpdate(updatedAlias, values.thumbnail_url);
          actions.setSubmitting(false);
          return Promise.resolve(updatedAlias);
        }).catch((err: ErrorInterface) => {
          setErrorNotification(err.message);
          actions.setSubmitting(false);
        });
      } catch (err) {
        setErrorNotification((err as ErrorInterface).message);
        actions.setSubmitting(false);
      }
    },
    [activeAlias?.uuid, journey?.uuid, onAliasUpdate, updateActiveLinkForm, setErrorNotification]
  );

  const onChangeSubmitting = (isSubmitting: boolean) => {
    setIsSaving(isSubmitting);
  };

  const formikParams = useMemo(
    () => ({
      initialValues: { ...activeLinkForm, slug: activeLinkForm?.slug || uuidv4().replace(/-/g, '').substring(0, 6) },
      validationSchema: validationSchema(!!activeLinkForm?.is_primary),
      onSubmit: onFormSubmit,
      enableReinitialize: false,
      validateOnBlur: false,
      validateOnChange: false,
    }),
    [onFormSubmit, activeLinkForm]
  );

  return (
    <JourneyFormik {...formikParams}>
      <JourneyForm onKeyPress={preventSubmitOnEnterPressed}>
        <FormikAutoSave
          key={`formik-auto-save-${activeAlias?.uuid}`}
          onChangeSubmitting={onChangeSubmitting}
          debounceMs={1000}
        />
        {children}
      </JourneyForm>
    </JourneyFormik>
  );
};

export default ShareFormik;
